import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, FormControl, InputLabel,
  Link as MuiLink, MenuItem, Paper, Select, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../contexts/AuthContext';
import {
  parseNotebook, publishNotebook, formatApiError,
  StudioParseResponse, StudioCheckpointSuggestion, StudioCellSpec, StudioPublishResponse,
  PresentationMode,
} from '../services/api';

const COMPARATORS = ['exact', 'numeric', 'set', 'regex', 'manual'] as const;

// Per-checkpoint editable draft, keyed by the cell's index.
interface CheckpointDraft {
  id: string;
  comparator: string;
  valueText: string;   // single edit surface; interpreted per comparator on publish
  tolerance: string;   // numeric only
  hint: string;
  revealAfter: string; // optional "reveal solution after N attempts"
}

type Role = 'checkpoint' | 'verbatim' | 'prompt' | 'keep' | 'exclude';

function isImportOnly(source: string): boolean {
  const lines = source.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return false;
  return lines.every((l) =>
    l.startsWith('import ') || l.startsWith('from ') ||
    l.startsWith('%') || l.startsWith('!') || l.startsWith('#'));
}

function suggestionValueText(s: StudioCheckpointSuggestion): string {
  const exp = s.expected;
  if (exp) {
    if (s.comparator === 'set' && Array.isArray(exp.value)) return exp.value.join(', ');
    if (s.comparator === 'regex' && exp.pattern != null) return String(exp.pattern);
    if (exp.value != null) return String(exp.value);
  }
  return s.expected_raw ? s.expected_raw : '';
}

// Build the comparator-specific expected payload the backend grader expects.
function buildExpected(d: CheckpointDraft): Record<string, any> | null {
  const v = d.valueText.trim();
  switch (d.comparator) {
    case 'manual':
      return null;
    case 'numeric':
      return { value: Number(v), tolerance: Number(d.tolerance || '0') };
    case 'regex':
      return { pattern: v };
    case 'set': {
      let items: any[];
      try {
        const parsed = JSON.parse(v);
        items = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        items = v.split(',').map((x) => {
          const t = x.trim();
          const n = Number(t);
          return t !== '' && !Number.isNaN(n) ? n : t;
        }).filter((x) => x !== '');
      }
      return { value: items };
    }
    case 'exact':
    default:
      return { value: v };
  }
}

function downloadB64(b64: string, filename: string) {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const blob = new Blob([arr], { type: 'application/x-ipynb+json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const CODE_BOX_SX = {
  fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap',
  wordBreak: 'break-word', m: 0, p: 1.25, borderRadius: 1, overflowX: 'auto',
} as const;

export default function Studio() {
  const { teacher, loading: authLoading } = useAuth();

  const [parsed, setParsed] = useState<StudioParseResponse | null>(null);
  const [lessonName, setLessonName] = useState('');
  const [retentionDays, setRetentionDays] = useState('');
  const [presentationMode, setPresentationMode] = useState<PresentationMode>('all');
  const [roles, setRoles] = useState<Record<number, Role>>({});
  const [drafts, setDrafts] = useState<Record<number, CheckpointDraft>>({});
  const [result, setResult] = useState<StudioPublishResponse | null>(null);

  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const handleFile = useCallback(async (file: File) => {
    setError(null);
    setUploading(true);
    try {
      const res = await parseNotebook(file);
      // Seed roles + checkpoint drafts from the detection result.
      const taskCells = new Set(
        res.suggestions.map((s) => s.task_cell_index).filter((i): i is number => i != null));
      const suggestionByCell = new Map(res.suggestions.map((s) => [s.cell_index, s]));
      const initialRoles: Record<number, Role> = {};
      const initialDrafts: Record<number, CheckpointDraft> = {};
      for (const c of res.cells) {
        const sugg = suggestionByCell.get(c.index);
        if (sugg) {
          initialRoles[c.index] = 'checkpoint';
          initialDrafts[c.index] = {
            id: sugg.checkpoint_id,
            comparator: sugg.comparator || 'exact',
            valueText: suggestionValueText(sugg),
            tolerance: sugg.expected?.tolerance != null ? String(sugg.expected.tolerance) : '0.001',
            hint: sugg.hint || '',
            revealAfter: sugg.reveal_after != null ? String(sugg.reveal_after) : '',
          };
        } else if (c.cell_type === 'markdown') {
          initialRoles[c.index] = taskCells.has(c.index) ? 'prompt' : 'keep';
        } else if (c.cell_type === 'code') {
          initialRoles[c.index] = isImportOnly(c.source) ? 'verbatim' : 'exclude';
        } else {
          initialRoles[c.index] = 'exclude';
        }
      }
      setRoles(initialRoles);
      setDrafts(initialDrafts);
      setLessonName(
        (res.filename || 'My lesson').replace(/\.ipynb$/i, '').replace(/[_-]+/g, ' ').trim());
      setParsed(res);
    } catch (e) {
      setError(formatApiError(e, 'Could not parse that notebook.'));
    } finally {
      setUploading(false);
    }
  }, []);

  const setRole = (index: number, role: Role) =>
    setRoles((r) => ({ ...r, [index]: role }));

  const updateDraft = (index: number, patch: Partial<CheckpointDraft>) =>
    setDrafts((d) => ({
      ...d,
      [index]: { ...(d[index] || {
        id: '', comparator: 'exact', valueText: '', tolerance: '0.001', hint: '', revealAfter: '',
      }), ...patch },
    }));

  const checkpointCount = useMemo(
    () => Object.values(roles).filter((r) => r === 'checkpoint').length, [roles]);

  const validationError = useMemo(() => {
    if (!parsed) return null;
    if (!lessonName.trim()) return 'Give the lesson a name.';
    if (checkpointCount === 0) return 'Mark at least one cell as a checkpoint.';
    const ids = new Set<string>();
    for (const c of parsed.cells) {
      if (roles[c.index] !== 'checkpoint') continue;
      const d = drafts[c.index];
      if (!d?.id?.trim()) return `Checkpoint on cell ${c.index + 1} needs an id.`;
      if (ids.has(d.id.trim())) return `Duplicate checkpoint id "${d.id.trim()}".`;
      ids.add(d.id.trim());
      if (d.comparator !== 'manual' && !d.valueText.trim())
        return `Checkpoint "${d.id}" needs an expected value (or use the "manual" comparator).`;
      if (d.comparator === 'numeric' && Number.isNaN(Number(d.valueText)))
        return `Checkpoint "${d.id}" expected value must be a number for the numeric comparator.`;
    }
    return null;
  }, [parsed, lessonName, roles, drafts, checkpointCount]);

  const handlePublish = async () => {
    if (!parsed || validationError) return;
    setError(null);
    setPublishing(true);
    try {
      const cells: StudioCellSpec[] = parsed.cells.map((c) => {
        const role = roles[c.index];
        if (role === 'checkpoint') {
          const d = drafts[c.index];
          return {
            index: c.index,
            role,
            checkpoint_id: d.id.trim(),
            comparator: d.comparator,
            expected: buildExpected(d),
            hint: d.hint.trim() || null,
            reveal_after_attempts: d.revealAfter ? Number(d.revealAfter) : null,
          };
        }
        return { index: c.index, role };
      });
      const res = await publishNotebook({
        lesson_name: lessonName.trim(),
        session_retention_days: retentionDays ? Number(retentionDays) : null,
        notebook: parsed.notebook,
        cells,
        presentation_mode: presentationMode,
      });
      setResult(res);
      downloadB64(res.student_notebook_b64, res.student_notebook_filename);
    } catch (e) {
      setError(formatApiError(e, 'Could not publish the lesson.'));
    } finally {
      setPublishing(false);
    }
  };

  const reset = () => {
    setParsed(null);
    setResult(null);
    setRoles({});
    setDrafts({});
    setLessonName('');
    setRetentionDays('');
    setError(null);
  };

  // --- Auth gate -----------------------------------------------------------
  if (authLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (!teacher) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', py: 6, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>Notebook Studio</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Sign in to upload a notebook and publish a lesson.
        </Typography>
        <Button component={RouterLink} to="/login" variant="contained">Sign in</Button>
      </Box>
    );
  }

  // --- Success screen ------------------------------------------------------
  if (result) {
    return (
      <Box sx={{ maxWidth: 680, mx: 'auto', py: 4 }}>
        <Typography variant="h5" gutterBottom>Lesson published 🎉</Typography>
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>{result.lesson_name}</Typography>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">Student join code</Typography>
              <Typography variant="h4" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                {result.join_code}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {result.checkpoint_ids.length} checkpoint(s) registered:{' '}
              {result.checkpoint_ids.join(', ')}
            </Typography>
          </Stack>
        </Paper>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => downloadB64(result.student_notebook_b64, result.student_notebook_filename)}
          >
            Download student notebook
          </Button>
          <Button
            variant="outlined"
            component={RouterLink}
            to={`/teacher/live?token=${encodeURIComponent(result.teacher_token)}`}
          >
            Open live dashboard
          </Button>
          <Button variant="text" onClick={reset}>Publish another</Button>
        </Stack>
        <Alert severity="info" sx={{ mt: 3 }}>
          Hand the student notebook (or the join code) to your class. The download
          started automatically — find it in your browser's downloads.
        </Alert>
      </Box>
    );
  }

  // --- Upload screen -------------------------------------------------------
  if (!parsed) {
    return (
      <Box sx={{ maxWidth: 680, mx: 'auto', py: 4 }}>
        <Typography variant="h5" gutterBottom>Notebook Studio</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Upload a notebook you've <strong>already run</strong> (so the answers are saved in
          the outputs). We'll detect the exercises; you review and edit them, then download a
          student version — no Python or markers required.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Paper
          variant="outlined"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) handleFile(f);
          }}
          onClick={() => fileInput.current?.click()}
          sx={{
            p: 6, textAlign: 'center', cursor: 'pointer',
            borderStyle: 'dashed', borderWidth: 2,
            borderColor: dragOver ? 'primary.main' : 'divider',
            bgcolor: dragOver ? 'action.hover' : 'transparent',
          }}
        >
          <input
            ref={fileInput}
            type="file"
            accept=".ipynb"
            hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
          {uploading ? (
            <Stack alignItems="center" spacing={2}>
              <CircularProgress />
              <Typography color="text.secondary">Parsing notebook…</Typography>
            </Stack>
          ) : (
            <Stack alignItems="center" spacing={1.5}>
              <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
              <Typography variant="h6">Drop your .ipynb here</Typography>
              <Typography variant="body2" color="text.secondary">or click to choose a file</Typography>
            </Stack>
          )}
        </Paper>
      </Box>
    );
  }

  // --- Designate screen ----------------------------------------------------
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5">Review &amp; designate</Typography>
        <Button size="small" onClick={reset}>Start over</Button>
      </Stack>

      {!parsed.any_outputs && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This notebook has no saved cell outputs — run it top-to-bottom and save before
          uploading, and we can prefill the expected answers for you. You can still enter
          them by hand below.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Lesson name"
            value={lessonName}
            onChange={(e) => setLessonName(e.target.value)}
            fullWidth
            size="small"
            required
          />
          <TextField
            label="Retention (days)"
            value={retentionDays}
            onChange={(e) => setRetentionDays(e.target.value.replace(/[^0-9]/g, ''))}
            size="small"
            sx={{ width: { xs: '100%', sm: 180 } }}
            placeholder="7"
            helperText="Optional · 1–365"
          />
          <FormControl size="small" sx={{ width: { xs: '100%', sm: 240 } }}>
            <InputLabel id="presentation-mode-label">Presenting mode</InputLabel>
            <Select
              labelId="presentation-mode-label"
              label="Presenting mode"
              value={presentationMode}
              onChange={(e) => setPresentationMode(e.target.value as PresentationMode)}
            >
              <MenuItem value="all">All submitted work</MenuItem>
              <MenuItem value="marked">Only marked work</MenuItem>
              <MenuItem value="off">Off</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {presentationMode === 'all'
            ? 'Presenting mode shows every code/plot a student or team submits, grouped by presenter.'
            : presentationMode === 'marked'
              ? 'Presenting mode shows only submissions marked for presentation — students mark their own (keep_submission), or you mark them on the dashboard.'
              : 'Presenting mode is disabled for this lesson.'}
        </Typography>
      </Paper>

      <Stack spacing={2}>
        {parsed.cells.map((cell) => {
          const role = roles[cell.index];
          const isCode = cell.cell_type === 'code';
          const draft = drafts[cell.index];
          return (
            <Paper
              key={cell.index}
              variant="outlined"
              sx={{
                p: 2,
                borderColor: role === 'checkpoint' ? 'primary.main' : 'divider',
                borderWidth: role === 'checkpoint' ? 2 : 1,
                opacity: role === 'exclude' ? 0.6 : 1,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={`Cell ${cell.index + 1}`} />
                  <Chip size="small" variant="outlined" label={cell.cell_type} />
                  {cell.has_output && (
                    <Chip size="small" variant="outlined" color="success" label="has output" />
                  )}
                </Stack>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    label="Role"
                    value={role}
                    onChange={(e) => setRole(cell.index, e.target.value as Role)}
                  >
                    {isCode
                      ? [
                          <MenuItem key="checkpoint" value="checkpoint">Checkpoint (exercise)</MenuItem>,
                          <MenuItem key="verbatim" value="verbatim">Include as-is</MenuItem>,
                          <MenuItem key="exclude" value="exclude">Exclude</MenuItem>,
                        ]
                      : [
                          <MenuItem key="prompt" value="prompt">Task prompt</MenuItem>,
                          <MenuItem key="keep" value="keep">Keep as-is</MenuItem>,
                          <MenuItem key="exclude" value="exclude">Exclude</MenuItem>,
                        ]}
                  </Select>
                </FormControl>
              </Stack>

              <Box component="pre" sx={{ ...CODE_BOX_SX, bgcolor: 'grey.100' }}>
                {cell.source || <em>(empty)</em>}
              </Box>
              {cell.output_preview && (
                <Box component="pre" sx={{ ...CODE_BOX_SX, bgcolor: 'grey.50', color: 'text.secondary', mt: 0.5 }}>
                  {cell.output_preview}
                </Box>
              )}

              {role === 'checkpoint' && draft && (
                <Box sx={{ mt: 1.5 }}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Stack spacing={1.5}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <TextField
                        label="Checkpoint id" size="small" fullWidth required
                        value={draft.id}
                        onChange={(e) => updateDraft(cell.index, { id: e.target.value })}
                      />
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Comparator</InputLabel>
                        <Select
                          label="Comparator" value={draft.comparator}
                          onChange={(e) => updateDraft(cell.index, { comparator: e.target.value as string })}
                        >
                          {COMPARATORS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Stack>
                    {draft.comparator !== 'manual' && (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField
                          label={draft.comparator === 'set' ? 'Expected values (comma-separated)'
                            : draft.comparator === 'regex' ? 'Expected pattern' : 'Expected answer'}
                          size="small" fullWidth
                          value={draft.valueText}
                          onChange={(e) => updateDraft(cell.index, { valueText: e.target.value })}
                          helperText={!draft.valueText && !parsed.any_outputs ? 'No saved output — type it' : ' '}
                        />
                        {draft.comparator === 'numeric' && (
                          <TextField
                            label="Tolerance" size="small" sx={{ width: { xs: '100%', sm: 140 } }}
                            value={draft.tolerance}
                            onChange={(e) => updateDraft(cell.index, { tolerance: e.target.value })}
                          />
                        )}
                      </Stack>
                    )}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <TextField
                        label="Hint (optional)" size="small" fullWidth
                        value={draft.hint}
                        onChange={(e) => updateDraft(cell.index, { hint: e.target.value })}
                      />
                      <Tooltip title="Let students reveal the solution after this many wrong attempts">
                        <TextField
                          label="Reveal after" size="small" sx={{ width: { xs: '100%', sm: 140 } }}
                          placeholder="off"
                          value={draft.revealAfter}
                          onChange={(e) => updateDraft(cell.index, { revealAfter: e.target.value.replace(/[^0-9]/g, '') })}
                        />
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Box>
              )}
            </Paper>
          );
        })}
      </Stack>

      <Paper
        elevation={3}
        sx={{
          position: 'sticky', bottom: 16, mt: 3, p: 2, borderRadius: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2">
            <strong>{checkpointCount}</strong> checkpoint(s) selected
          </Typography>
          {validationError && (
            <Typography variant="caption" color="error">{validationError}</Typography>
          )}
        </Box>
        <Button
          variant="contained"
          disabled={!!validationError || publishing}
          onClick={handlePublish}
          startIcon={publishing ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
        >
          {publishing ? 'Publishing…' : 'Publish & download'}
        </Button>
      </Paper>
    </Box>
  );
}
